/**
 * Chart Helper Utilities
 *
 * Utility functions for consistent Chart.js configuration and styling
 * across Financial Insights dashboard charts.
 */

import { Theme } from '@mui/material/styles';
import { ChartOptions } from 'chart.js';

/**
 * Primary color palette for charts
 * Based on Material-UI default theme
 */
export const CHART_COLORS = [
  '#1976d2', // Primary blue
  '#64b5f6', // Light blue
  '#0d47a1', // Dark blue
  '#2E86AB', // Blue
  '#A23B72', // Purple
  '#F18F01', // Orange
  '#C73E1D', // Red-orange
  '#06A77D', // Teal
  '#5C946E', // Green
  '#B5838D', // Rose
];

/**
 * Aging bucket color coding
 * Green (good) → Yellow (warning) → Orange (urgent) → Red (critical)
 */
export const AGING_BUCKET_COLORS = {
  '0-30': '#4CAF50', // Green - good standing
  '31-60': '#FFC107', // Yellow - warning
  '61-90': '#FF9800', // Orange - urgent attention needed
  '90+': '#F44336', // Red - critical
};

/**
 * Get default Chart.js options with theme-aware styling
 *
 * @param theme - Material-UI theme object
 * @param overrides - Optional chart option overrides
 * @returns Complete ChartOptions configuration
 */
export const getDefaultChartOptions = (
  theme: Theme,
  overrides?: Partial<ChartOptions>
): ChartOptions => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        usePointStyle: true,
        padding: 20,
        color: theme.palette.text.primary,
        font: {
          family: 'Arial, sans-serif',
          size: 12,
        },
      },
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
      padding: 12,
      cornerRadius: 4,
      displayColors: true,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: theme.palette.text.secondary,
        font: {
          family: 'Arial, sans-serif',
          size: 11,
        },
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        color:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
      },
      ticks: {
        color: theme.palette.text.secondary,
        font: {
          family: 'Arial, sans-serif',
          size: 11,
        },
      },
    },
  },
  ...overrides,
});

/**
 * Format currency for chart labels and tooltips
 *
 * @param value - Numeric value to format
 * @param currency - Currency code (default: USD)
 * @param compact - Use compact notation for large numbers (default: false)
 * @returns Formatted currency string
 */
export const formatChartCurrency = (
  value: number,
  currency: string = 'USD',
  compact: boolean = false
): string => {
  if (compact && Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(value);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Generate color palette for charts
 *
 * @param count - Number of colors needed
 * @returns Array of color hex codes
 */
export const getChartColors = (count: number): string[] => {
  if (count <= CHART_COLORS.length) {
    return CHART_COLORS.slice(0, count);
  }

  // If we need more colors than available, cycle through the palette
  const colors: string[] = [];
  for (let i = 0; i < count; i += 1) {
    colors.push(CHART_COLORS[i % CHART_COLORS.length]);
  }
  return colors;
};

/**
 * Get color for aging bucket based on label
 *
 * @param label - Aging bucket label (e.g., "0-30 days", "31-60 days")
 * @returns Color hex code
 */
export const getAgingBucketColor = (label: string): string => {
  if (label.includes('0-30') || label.includes('0–30')) {
    return AGING_BUCKET_COLORS['0-30'];
  }
  if (label.includes('31-60') || label.includes('31–60')) {
    return AGING_BUCKET_COLORS['31-60'];
  }
  if (label.includes('61-90') || label.includes('61–90')) {
    return AGING_BUCKET_COLORS['61-90'];
  }
  if (label.includes('90+') || label.includes('90') || label.includes('over')) {
    return AGING_BUCKET_COLORS['90+'];
  }
  // Default to primary color if no match
  return CHART_COLORS[0];
};

/**
 * Format date for chart labels
 *
 * @param dateString - ISO date string
 * @param granularity - Time granularity (daily, weekly, monthly)
 * @returns Formatted date string
 */
export const formatChartDate = (
  dateString: string,
  granularity: 'daily' | 'weekly' | 'monthly' = 'monthly'
): string => {
  const date = new Date(dateString);

  switch (granularity) {
    case 'daily':
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
      }).format(date);
    case 'weekly':
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
      }).format(date);
    case 'monthly':
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: 'numeric',
      }).format(date);
    default:
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date);
  }
};

/**
 * Calculate percentage of total
 *
 * @param value - Individual value
 * @param total - Total sum
 * @returns Percentage string (e.g., "25.5%")
 */
export const calculatePercentage = (value: number, total: number): string => {
  if (total === 0) return '0%';
  const percentage = (value / total) * 100;
  return `${percentage.toFixed(1)}%`;
};

/**
 * Truncate long labels for chart display
 *
 * @param label - Original label
 * @param maxLength - Maximum length (default: 20)
 * @returns Truncated label with ellipsis if needed
 */
export const truncateLabel = (
  label: string,
  maxLength: number = 20
): string => {
  if (label.length <= maxLength) return label;
  return `${label.substring(0, maxLength - 3)}...`;
};
